import { Autocomplete, Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { ts } from "src/utils/translate";
import { useCreateLabelMutation, useDeleteUserLabelMutation, useGetLabelsQuery } from "src/api/v2/user-v2-ApiSlice";
import { UserLabels } from "src/model/api/v2/data-contracts";
import { pastelPairs } from "src/utils/labelColors";
import { BaseDialog } from "src/components/BaseDialog/BaseDialog";
import { LabelChip } from "src/pages/ProjectDetailsWizard/LabelChip";
import { useAppDispatch } from "src/app/hooks";
import { showSnackBar } from "src/api/ui-slice";

interface ProjectWizardLabelsProps {
    formData: any;
    setFormData: (data: any) => void;
}

export const ProjectWizardLabels = ({ formData, setFormData }: ProjectWizardLabelsProps) => {
    const [selectedColor, setSelectedColor] = useState(pastelPairs[0]);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [noMatch, setNoMatch] = useState(false);

    const { data: labelsData, isLoading } = useGetLabelsQuery();
    const [createLabel, { isLoading: isCreating }] = useCreateLabelMutation();
    const [allLabelOptions, setAllLabelOptions] = useState<UserLabels[]>([]);

    const [selectedLabel, setSelectedLabel] = useState<UserLabels>();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [deleteUserLabel] = useDeleteUserLabelMutation();
    const dispatch = useAppDispatch();

    const handleDelete = () => {
        if (!selectedLabel) return;
        deleteUserLabel({ labelId: selectedLabel.id })
            .unwrap()
            .then(() => {
                setIsDeleteDialogOpen(false);
            })
            .catch(() => {
                dispatch(showSnackBar({ text: ts('delete_label_error', 'Failed to delete tag'), severity: 'error' }));
            });
    };

    useEffect(() => {
        if (labelsData) {
            setAllLabelOptions(labelsData.labels);
        }
    }, [labelsData]);

    useEffect(() => {
        const val = inputValue.trim();
        if (!val) {
            setNoMatch(false);
            return;
        }
        const hasMatch = allLabelOptions.some(l =>
            l.name.toLowerCase().includes(val.toLowerCase())
        );
        setNoMatch(!hasMatch);
        setOpen(true);
    }, [inputValue, allLabelOptions]);

    const showColorDot = noMatch && !!inputValue.trim();

    const saveNewLabel = () => {
        if (!noMatch) return;
        const newLabelName = inputValue.trim();
        if (!newLabelName) return;

        return createLabel({
            label_name: newLabelName,
            label_color: selectedColor.bg
        }).then((response) => {
            const createdLabel = response.data?.label;
            if (!createdLabel) return;
            setFormData((prev: { tags: any[] }) => ({
                ...prev,
                tags: [...(prev.tags || []), createdLabel]
            }));
            setInputValue('');
            setNoMatch(false);
        });
    };

    const handleKeyDown = async (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            await saveNewLabel();
            setOpen(true);
        }
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <Autocomplete
                multiple
                freeSolo
                id="tags-autocomplete"
                options={allLabelOptions.filter(
                    opt => !(formData.tags || []).some((t: { name: string; }) => t.name === opt.name)
                )}
                getOptionLabel={(option) => option.name}
                loading={isLoading || isCreating}
                value={formData.tags || []}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                onChange={(_, newValue) => {
                    const currentTags = formData.tags || [];
                    const filtered = newValue.filter(v => typeof v !== 'string');
                    if (filtered.length !== currentTags.length) {
                        setFormData({ ...formData, tags: filtered });
                    }
                }}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (reason === 'blur' || reason === 'escape') setOpen(false);
                }}
                disableCloseOnSelect
                renderValue={(value: UserLabels[], getItemProps) =>
                    value.map((label, index) => (
                        <LabelChip
                            {...getItemProps({ index })}
                            key={label.id ?? label.name}
                            labelData={label}
                        />
                    ))
                }
                renderOption={(props, label: UserLabels) => (
                    <li {...props} key={label.id ?? label.name}>
                        <LabelChip
                            labelData={label}
                            onDelete={allLabelOptions.length > 1 ? () => {
                                setSelectedLabel(label);
                                setIsDeleteDialogOpen(true);
                            } : undefined}
                        />
                    </li>
                )}
                slotProps={{
                    listbox: {
                        sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'flex-start',
                            maxHeight: 200,
                            overflowY: 'auto',
                            '& .MuiAutocomplete-option': {
                                minHeight: 'unset',
                                padding: 0,
                                backgroundColor: 'transparent !important',
                                '&:hover': {
                                    backgroundColor: 'transparent !important',
                                },
                            },
                        },
                    },
                }}
                noOptionsText={noMatch ? 'Press Enter to save new label' : 'No results'}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        label={ts('project_tag_label', 'Tags')}
                        size="small"
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    showColorDot && (
                                        <>
                                            <Box
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => setColorPickerOpen(prev => !prev)}
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50% !important',
                                                    backgroundColor: selectedColor.bg,
                                                    cursor: 'pointer',
                                                    border: `1px solid ${selectedColor.contrast}`,
                                                    marginRight: '8px',
                                                }}
                                            />
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                ),
                            },
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setOpen(true)}
                        onBlur={saveNewLabel}
                    />
                )}
                fullWidth
            />

            {colorPickerOpen && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 1,
                        p: 1,
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 24px)',
                        gap: 1,
                        zIndex: 10
                    }}
                >
                    {pastelPairs.map((color) => (
                        <Box
                            key={color.bg}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                setSelectedColor(color);
                                setColorPickerOpen(false);
                            }}
                            sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50% !important',
                                backgroundColor: color.bg,
                                cursor: 'pointer',
                                border: `1px solid ${color.contrast}`,
                            }}
                        />
                    ))}
                </Box>
            )}

            {isDeleteDialogOpen && (
                <BaseDialog
                    onClose={() => setIsDeleteDialogOpen(false)}
                    title={ts("you_want_to_delete_this_tag", "Are you sure you want to delete this tag?")}
                    cancelLabel={ts("plt_cancel", "Cancel")}
                    actionLabel={ts("plt_confirm", "Confirm")}
                    onAction={handleDelete}
                >
                    {ts("it_will_removed_from_all_projects", "If you proceed, it will be removed from all your existing projects.")}
                </BaseDialog>
            )}
        </Box>
    );
};
